<template>
  <customDialog
    v-model="show"
    title="close_shift"
    width="400px"
    :footer="false"
    :closeBtn="true"
  >
    <template #activator>
      <v-alert v-if="forceLogout" type="warning" outlined dense class="mb-3">
        <div class="text-body-2">
          <v-icon left small>fas fa-exclamation-triangle</v-icon>
          {{ $t("must_clock_out_before_logout") }}
        </div>
      </v-alert>

      <v-alert v-if="shift" type="info" outlined dense class="mb-3">
        <div class="text-body-2">
          <div>
            {{ $t("clocked_in_at") }}:
            {{ shift.clock_in_at | moment("DD-MM-YYYY HH:mm") }}
          </div>
          <div>
            {{ $t("opening_cash") }}: {{ shift.opening_cash | NumberFormat }}
          </div>
          <div>
            {{ $t("expected_cash") }}: {{ expectedCash | NumberFormat }}
          </div>
        </div>
      </v-alert>

      <!-- ClockInNumpad for closing cash entry -->
      <ClockInNumpad
        :value="closingCashString"
        @input="handleCashInput"
        @submit="submit"
        :loading="loading"
        :disabled="!canSubmit"
        buttonText="clock_out"
        label="closing_cash"
      />
    </template>
  </customDialog>
</template>

<script>
import { mapActions } from "vuex";
import ClockInNumpad from "@/components/shift/ClockInNumpad";
import customDialog from "@/components/customDialog";
import { required, minValue } from "vuelidate/lib/validators";
import { success } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";

export default {
  name: "ClockOutDialog",
  mixins: [myMixin],
  components: { ClockInNumpad, customDialog },
  props: {
    value: Boolean,
    shift: {
      type: Object,
      default: null,
    },
    forceLogout: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    form: {
      closing_cash: "0",
    },
    loading: false,
  }),
  validations: {
    form: {
      closing_cash: { required, minValue: minValue(0) },
    },
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    // String representation for ClockInNumpad
    closingCashString() {
      return this.form.closing_cash || "0";
    },
    // Enable submit when closing cash is greater than 0
    canSubmit() {
      const cash = Number.parseFloat(this.form.closing_cash) || 0;
      return cash > 0;
    },
    expectedCash() {
      if (!this.shift) return 0;
      const opening = Number.parseFloat(this.shift.opening_cash) || 0;
      const sales = Number.parseFloat(this.shift.total_sales) || 0;
      const cashIn = Number.parseFloat(this.shift.cash_in_total) || 0;
      const cashOut = Number.parseFloat(this.shift.cash_out_total) || 0;
      return opening + sales + cashIn - cashOut;
    },
  },
  methods: {
    ...mapActions({ clockOut: "index/clockOut" }),
    handleCashInput(value) {
      this.form.closing_cash = value;
      // Trigger validation when cash input changes
      this.$v.form.closing_cash.$touch();
    },
    close() {
      this.show = false;
      this.$v.form.$reset();
      this.form = { closing_cash: "0" };
    },
    async submit() {
      if (!this.canSubmit) return;

      this.$v.form.$touch();
      const closingCash = Number.parseFloat(this.form.closing_cash) || 0;
      if (!this.$v.form.closing_cash.required || closingCash <= 0) {
        this.$toast.error(this.$t("this_field_is_required"));
        return;
      }

      if (!this.shift || !this.shift.id) {
        this.$toast.error(this.$t("no_active_shift"));
        return;
      }

      try {
        this.loading = true;
        const result = await this.clockOut({
          that: this,
          shiftId: this.shift.id,
          closingCash: closingCash,
        });
        await success(this, "clock_out_success");
        this.$emit("success", result?.data?.data || null);
        this.close();
      } catch (error) {
        this.$toast.error(
          error.response?.data?.message || this.$t("error_occurred")
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
